import type { CurrentUser } from '@zhiyue/core';
import React from 'react';
import {
  type OpenPlatformMember,
  listOrganizationMembers,
} from '@/services/openPlatformMember';
import { fetchOpenPlatformToken } from '@/services/openPlatformToken';

export type { OpenPlatformMember };

export type MemberDirectoryUser = Pick<
  CurrentUser,
  'tenantUuid' | 'memberUuid'
> & {
  organizationUuid?: string;
};

let identityKey = '';
let loading: Promise<void> | null = null;
const members = new Map<string, OpenPlatformMember>();
const listeners = new Set<() => void>();

const notifyListeners = () => {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // 忽略监听器异常
    }
  });
};

const getIdentityKey = (currentUser?: Partial<MemberDirectoryUser> | null) =>
  [
    currentUser?.tenantUuid || currentUser?.organizationUuid,
    currentUser?.organizationUuid,
    currentUser?.memberUuid,
  ]
    .map((value) => String(value || '').trim())
    .join('|');

export const clearMemberDirectory = () => {
  identityKey = '';
  loading = null;
  members.clear();
  notifyListeners();
};

export const loadMemberDirectory = async (
  currentUser?: Partial<MemberDirectoryUser> | null,
): Promise<void> => {
  const nextIdentityKey = getIdentityKey(currentUser);
  const memberUuid = String(currentUser?.memberUuid || '').trim();
  if (!nextIdentityKey || !memberUuid) {
    clearMemberDirectory();
    return;
  }
  if (identityKey === nextIdentityKey && (loading || members.size)) {
    return loading || undefined;
  }

  identityKey = nextIdentityKey;
  members.clear();
  notifyListeners();

  loading = fetchOpenPlatformToken(currentUser as MemberDirectoryUser)
    .then(({ accessToken }) => listOrganizationMembers(accessToken, memberUuid))
    .then((items) => {
      if (identityKey !== nextIdentityKey) return;
      items.forEach((member) => {
        const key = member.memberUuid || member.id;
        if (key) {
          members.set(key, member);
        }
      });
      notifyListeners();
    })
    .finally(() => {
      if (identityKey === nextIdentityKey) {
        loading = null;
      }
    });

  return loading;
};

export const getMemberName = (
  memberUuid?: string | null,
  fallback?: string | null,
): string => {
  if (!memberUuid) {
    return fallback || '—';
  }
  const member = members.get(memberUuid);
  if (member?.name) {
    return member.name;
  }
  return fallback || memberUuid;
};

export const getMember = (
  memberUuid?: string | null,
): OpenPlatformMember | undefined => {
  if (!memberUuid) return undefined;
  return members.get(memberUuid);
};

export const getAllMembers = (): OpenPlatformMember[] => {
  const uniqueMembers = new Map<string, OpenPlatformMember>();
  members.forEach((member) => {
    const key = member.memberUuid || member.id;
    if (key && !uniqueMembers.has(key)) {
      uniqueMembers.set(key, member);
    }
  });
  return Array.from(uniqueMembers.values());
};

export const useMemberDirectory = () => {
  const [, setTick] = React.useState(0);

  React.useEffect(() => {
    const listener = () => setTick((v) => v + 1);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  return {
    members: getAllMembers(),
    getMemberName,
    getMember,
    loading: Boolean(loading),
  };
};
